import * as i0 from '@angular/core';
import { Injectable, NgModule } from '@angular/core';
import { BQXConfiguration } from 'bqx-ui-common';
import * as i1 from '@angular/common/http';
import { HttpHeaders, HttpParams, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { throwError, EMPTY } from 'rxjs';

var BusinessTypeSource;
(function (BusinessTypeSource) {
    BusinessTypeSource["CLBECA"] = "ClBecaDataService";
    BusinessTypeSource["FALLBACK"] = "FallbackDataService";
})(BusinessTypeSource || (BusinessTypeSource = {}));
var BusinessTypeValidator;
(function (BusinessTypeValidator) {
    BusinessTypeValidator["BOLDPENGUIN"] = "BP-SDK";
})(BusinessTypeValidator || (BusinessTypeValidator = {}));
var RequestType;
(function (RequestType) {
    RequestType["INIT"] = "INIT";
    RequestType["SEARCH"] = "SEARCH";
    RequestType["CLEAN"] = "CLEAN";
})(RequestType || (RequestType = {}));

/**
 * @class BusinessTypeRequestData
 * @description Generic Class to Instantiate IWatsonType Request Data for Business Type Service
 */
class BusinessTypeRequestData {
    constructor(val) {
        this.data = val;
    }
}

class CLBecaDataService {
    get defaultRequestData() {
        return {
            headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
            requestBody: {}
        };
    }
    constructor(http) {
        this.http = http;
    }
    initDataService(options) {
        this.updateRequestData(options);
        this.validateRequest(RequestType.INIT);
        return this.postClBeca();
    }
    getBusinessTypes(options) {
        this.updateRequestData(options);
        this.validateRequest(RequestType.SEARCH);
        return this.postClBeca();
    }
    cleanSearchHistory(options) {
        this.updateRequestData(options);
        this.validateRequest(RequestType.CLEAN);
        return this.postClBeca();
    }
    validateRequest(requestType) {
        const data = this._requestData.data.requestBody;
        switch (requestType) {
            case RequestType.INIT:
                if (data) {
                    if (data.input.text !== '' && data.context.conversation_id !== '' && data.context.guess_count === 0)
                        throw throwError('DataNode is not empty.');
                }
                else {
                    throw throwError('DataNode not provided.');
                }
                break;
            case RequestType.SEARCH:
            case RequestType.CLEAN:
                if (data) {
                    if (data.input.text === '' || data.context.conversation_id === '' || data.quote_number === '')
                        throw throwError('Input Error.');
                }
                else {
                    throw throwError('DataNode not provided.');
                }
                break;
            default:
                break;
        }
    }
    postClBeca() {
        return this.http.post(BQXConfiguration.data.businessTypePrimaryClientURL, this.generateBody(), {
            headers: this.generateHeaders()
        });
    }
    updateRequestData(options) {
        const { headers, requestBody } = options && options !== null ? options : this.defaultRequestData;
        this._requestData = new BusinessTypeRequestData({ headers, requestBody });
    }
    generateHeaders() {
        const headers = new HttpHeaders(this._requestData.data.headers);
        return headers;
    }
    generateBody() {
        const result = JSON.stringify(this._requestData.data.requestBody);
        return result;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: CLBecaDataService, deps: [{ token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: CLBecaDataService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: CLBecaDataService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.HttpClient }] });

class BpSdkBusinessTypeMatchService {
    get httpHeaders() {
        return new HttpHeaders({
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this._apiToken}`,
        });
    }
    constructor(http) {
        this.http = http;
    }
    findBusinessType(searchObj) {
        const { apiUrl = '', apiToken = '', questioId = '', searchPhrase = '' } = searchObj || {};
        this._apiToken = apiToken;
        if ([apiUrl, questioId].every((item) => item !== '')) {
            return this.searchChoices(apiUrl, questioId, searchPhrase);
        }
        else {
            return EMPTY;
        }
    }
    searchChoices(apiUrl, question_id, filterValue) {
        const url = `${apiUrl}/questions/${question_id}/choices`;
        const params = this.createHttpParams({ filter: filterValue });
        const options = {
            headers: this.httpHeaders,
            params,
        };
        return this.http.get(url, { ...options, observe: 'response' });
    }
    createHttpParams(params) {
        let httpParams = new HttpParams();
        Object.keys(params).forEach(param => {
            if (params[param]) {
                httpParams = httpParams.set(param, params[param]);
            }
        });
        return httpParams;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BpSdkBusinessTypeMatchService, deps: [{ token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BpSdkBusinessTypeMatchService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BpSdkBusinessTypeMatchService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.HttpClient }] });

class FallbackBusinessDataService {
    get defaultRequestData() {
        return { headers: { 'Content-Type': 'application/json' }, requestBody: {} };
    }
    constructor(http) {
        this.http = http;
    }
    getBusinessTypes(options) {
        this.updateRequestData(options);
        return this.getFallback();
    }
    updateRequestData(options) {
        const { headers, requestBody } = (options && options !== null) ? options :
            this.defaultRequestData;
        this._requestData = new BusinessTypeRequestData({ headers, requestBody });
    }
    getBusinessClassifications(businessTypes) {
        const options = new Array();
        businessTypes.forEach((naicsBusClass) => {
            options.push({
                Value: naicsBusClass.NaicsDescription,
                Text: naicsBusClass.ProgressiveClass
            });
        });
        return options;
    }
    generateHeaders() {
        const headers = new HttpHeaders(this._requestData.data.headers);
        return headers;
    }
    getFallback() {
        return this.http.get(BQXConfiguration.data.businessTypeSecondaryClientURL + this.formatForDynamoStringMatch(this._requestData.data.requestBody.input), { headers: this.generateHeaders(), responseType: 'json' });
    }
    formatForDynamoStringMatch(val) {
        try {
            return val.replace(/^./, val[0].toUpperCase());
        }
        catch (error) {
            console.error(error);
            return val;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: FallbackBusinessDataService, deps: [{ token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: FallbackBusinessDataService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: FallbackBusinessDataService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.HttpClient }] });

class BusinessTypeRequestsService {
    get businessTypeRequest() {
        this.setPrimaryBusinessTypeRequest();
        return this._businessTypeRequest;
    }
    setPrimaryBusinessTypeRequest() {
        switch (BQXConfiguration.data.businessTypePrimaryClient) {
            case BusinessTypeSource.CLBECA: {
                this._businessTypeRequest.primary = new CLBecaDataService(this.http);
                break;
            }
            default: {
                this._businessTypeRequest.primary = null;
                break;
            }
        }
    }
    get businessTypeValidator() {
        return this._businessTypeValidator;
    }
    constructor(http) {
        this.http = http;
        this._businessTypeRequest = { primary: null, secondary: null };
        //if we add other businessTypeSecondaryClient or businessTypeValidatorClient they will need to move out of the constructor
        switch (BQXConfiguration.data.businessTypeSecondaryClient) {
            case BusinessTypeSource.FALLBACK: {
                this._businessTypeRequest.secondary = new FallbackBusinessDataService(this.http);
                break;
            }
            default: {
                this._businessTypeRequest.secondary = null;
                break;
            }
        }
        switch (BQXConfiguration.data.businessTypeValidatorClient) {
            case BusinessTypeValidator.BOLDPENGUIN: {
                this._businessTypeValidator = new BpSdkBusinessTypeMatchService(this.http);
                break;
            }
            default: {
                this._businessTypeValidator = null;
                break;
            }
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BusinessTypeRequestsService, deps: [{ token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BusinessTypeRequestsService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BusinessTypeRequestsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: i1.HttpClient }] });

class BusinessTypeRequestsAbstractService {
}

class BqxBusinesstypeRequestsModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BqxBusinesstypeRequestsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.8", ngImport: i0, type: BqxBusinesstypeRequestsModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BqxBusinesstypeRequestsModule, providers: [provideHttpClient(withInterceptorsFromDi())] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BqxBusinesstypeRequestsModule, decorators: [{
            type: NgModule,
            args: [{ declarations: [],
                    exports: [], imports: [], providers: [provideHttpClient(withInterceptorsFromDi())] }]
        }] });

/*
 * Public API Surface of bqx-businesstype-requests
 */

/**
 * Generated bundle index. Do not edit.
 */

export { BqxBusinesstypeRequestsModule, BusinessTypeRequestData, BusinessTypeRequestsAbstractService, BusinessTypeRequestsService };
